import { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { API, authHeader, fmt, decisionCls } from "@/lib/api";
import { localTimeKey } from "@/lib/terminalUtils";

export const SignalCard = ({ sig, balance, riskPct }) => {
  const [exp, setExp] = useState("");
  const [loading, setLoading] = useState(false);

  const explain = async () => {
    setExp("");
    setLoading(true);
    try {
      const resp = await fetch(`${API}/signals/${sig.symbol}/explain`, {
        method: "POST",
        headers: authHeader(),
      });
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const d = line.slice(6);
          if (d === "[DONE]") continue;
          try {
            setExp((prev) => prev + JSON.parse(d).t);
          } catch { /* skip */ }
        }
      }
    } catch {
      setExp("[Gagal memuat penjelasan AI]");
    } finally {
      setLoading(false);
    }
  };

  const up = (sig.change_pct ?? 0) >= 0;

  return (
    <div className="border border-white/10 bg-ink-surface p-5" data-testid={`signal-card-${sig.symbol.toLowerCase()}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-base font-bold text-white">{sig.symbol}</span>
            {sig.direction && (
              <span className={`font-mono text-xs font-bold ${sig.direction === "BUY" ? "text-buy" : "text-sell"}`}>
                {sig.direction === "BUY" ? "▲ BUY" : "▼ SELL"}
              </span>
            )}
          </div>
          <div className="mt-1 font-mono text-sm text-mute">
            {fmt(sig.price, sig.symbol === "BTCUSD" ? 1 : sig.symbol === "XAUUSD" ? 2 : 5)}
            <span className={`ml-2 text-xs ${up ? "text-buy" : "text-sell"}`}>
              {up ? "+" : ""}{sig.change_pct}% /24j
            </span>
          </div>
        </div>
        <span className={`border px-2.5 py-1 font-mono text-[10px] font-bold ${decisionCls(sig.decision)}`} data-testid={`decision-${sig.symbol.toLowerCase()}`}>
          {sig.decision === "TRADE" ? sig.label : sig.decision}
        </span>
      </div>
      {sig.news_condition && (
        <span className={`mt-2 inline-block border px-2 py-0.5 font-mono text-[9px] font-bold ${
          sig.news_condition === "HIGH RISK" ? "border-sell/50 bg-sell/10 text-sell"
          : sig.news_condition === "CAUTION" ? "border-warn/40 bg-warn/10 text-warn"
          : "border-buy/40 bg-buy/10 text-buy"}`} data-testid={`news-ctx-${sig.symbol.toLowerCase()}`}>
          NEWS: {sig.news_condition}
        </span>
      )}

      <div className="mt-4 flex items-end gap-2">
        <span className={`font-display text-4xl font-black ${sig.total >= 70 ? "text-buy" : sig.total >= 60 ? "text-warn" : "text-sell"}`}>
          {sig.total}
        </span>
        <span className="mb-1.5 font-mono text-[10px] text-mute-dim">/100 confidence</span>
      </div>

      <div className="mt-3 space-y-1.5">
        {sig.components.map((c) => (
          <div key={c.name} className="flex items-center gap-2" title={c.note}>
            <span className="w-24 shrink-0 truncate font-mono text-[9px] text-mute-dim sm:w-32 sm:text-[10px]">{c.name}</span>
            <div className="h-1 flex-1 bg-white/5">
              <div
                className={`h-full ${c.got / c.max > 0.6 ? "bg-buy" : c.got / c.max > 0.35 ? "bg-warn" : "bg-sell"}`}
                style={{ width: `${(c.got / c.max) * 100}%` }}
              />
            </div>
            <span className="w-8 text-right font-mono text-[9px] text-mute-dim">{c.got}/{c.max}</span>
          </div>
        ))}
      </div>

      {sig.decision === "TRADE" && sig.entry && (
        <div className="mt-4 grid grid-cols-3 gap-px border border-white/10 bg-white/10 font-mono text-xs">
          {[["ENTRY", sig.entry, "text-white"], ["SL", sig.sl, "text-sell"], ["TP", sig.tp, "text-buy"]].map(([k, v, cls]) => (
            <div key={k} className="bg-ink px-2 py-2 text-center">
              <div className="text-[9px] text-mute-dim">{k}</div>
              <div className={`font-bold ${cls}`}>{v}</div>
            </div>
          ))}
        </div>
      )}

      {sig.decision === "TRADE" && sig.entry && sig.sl && balance != null && (
        <p className="mt-3 font-mono text-[10px] text-mute" data-testid={`ideal-size-${sig.symbol.toLowerCase()}`}>
          &gt; posisi ideal:{" "}
          <b className="text-white">
            {(() => {
              const riskAmt = (balance * (riskPct ?? 1)) / 100;
              const dist = Math.abs(sig.entry - sig.sl);
              const units = dist > 0 ? riskAmt / dist : 0;
              return units >= 100 ? units.toFixed(0) : units.toFixed(2);
            })()} unit
          </b>{" "}
          · risiko ${(((balance * (riskPct ?? 1)) / 100)).toFixed(2)} ({riskPct ?? 1}%)
        </p>
      )}

      {sig.reasons?.length > 0 && (
        <div className="mt-4 border-l-2 border-sell/60 pl-3">
          {sig.reasons.slice(0, 3).map((r) => (
            <p key={r} className="font-mono text-[10px] leading-relaxed text-mute">· {r}</p>
          ))}
        </div>
      )}

      <button
        onClick={explain}
        disabled={loading}
        data-testid={`explain-button-${sig.symbol.toLowerCase()}`}
        className="mt-4 flex items-center gap-2 border border-brand/50 bg-brand/10 px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-wider text-[#8b97f5] transition-colors duration-200 hover:bg-brand/25 disabled:opacity-60"
      >
        {loading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
        Penjelasan AI
      </button>
      {exp && (
        <p className="mt-3 border border-white/10 bg-ink p-3 font-mono text-[11px] leading-relaxed text-mute" data-testid={`explanation-${sig.symbol.toLowerCase()}`}>
          {exp}
        </p>
      )}
      <p className="mt-3 font-mono text-[9px] text-mute-dim">diperbarui: {sig.updated_at ? localTimeKey(sig.updated_at) : "—"}</p>
    </div>
  );
};
